/* eslint-disable no-extra-boolean-cast */
import React, { useRef, useCallback, useEffect, useReducer, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as R from "ramda";
import * as yup from "yup";
import { Formik, Form } from "formik";
import {
  Heading,
  Box,
  Divider,
  AbsoluteCenter,
  Text,
  Stack,
  Button,
  Alert,
  AlertIcon,
  useColorModeValue,
  VisuallyHiddenInput,
} from "@chakra-ui/react";

import { finishCreateCCRebilly } from "api/payments";
import { logInfo, logError } from "api/accounts-customer";
import { FullPageSpinner } from "components/Spinner";
import { AddCCLegalese } from "components/Legalese";
import { formatErrorMessages, TextFieldGroup as TextField } from "components/Form";
import { isNilOrEmpty, useSessionJWT, useSessionCtx } from "utils";
import { countryHasStates, isValidState, toStateISO } from "utils/regions";
import RebillyCardTextField, { validateRebillyField, getErrorCodeFromState } from "./TextField";
import { ThreeDS2, requires3DS } from "./ThreeDS2";
import AcceptedCreditCards from "./AcceptedCreditCards";
import AddressWithPlacesAutocomplete from "components/Form/AddressWithPlacesAutocomplete";
import { makeGooglePlaceInputValidation } from "components/Form/AddressWithPlacesAutocomplete/utils";

import { useLanguageContext } from "utils/LanguageProvider";
import { isRebillyControllerReady } from "./utils";

import AddPlaidACHButton from "containers/Pages/PlaidACH/AddPlaidACHButton";
import { AddPaypalButton } from "components/Paypal";

const InnerForm = ({
  // formikProps ----
  isSubmitting,
  isValid,
  errors,
  values,
  validateForm,
  handleChange,
  // ----------------
  formState,
  dispatch,
  // ----------------
  rebillyLib,
  publicKey,
  onReady,
  config = {},
  formRef,
  submitError,
  onCancel,
}) => {
  const { formatMessage } = useIntl();
  const { locale } = useLanguageContext();
  const { debug } = useSessionCtx();
  const [rebillyReady, setRebillyReady] = useState(false);

  // Because we're passing color values through to Rebilly's FramePay, we need to generate the
  // colormode aware hex value here and pass it through to rebillyLib.initialize below.
  const inputColor = useColorModeValue("#1B2228", "white");
  const placeholderColor = useColorModeValue("rgba(0, 0, 0, 0.4)", "rgba(255, 255, 255, 0.4)");
  const errorColor = useColorModeValue("#E53E3E", "#FC8181"); // red.300 and red.500 (same as error colors elsewhere)

  const addOtherPMs = config.addPaypal || config.addACH;

  useEffect(() => {
    if (!rebillyLib.initialized) {
      rebillyLib.initialize({
        locale,
        publishableKey: publicKey,
        style: {
          base: {
            color: inputColor,
            "::placeholder": {
              color: placeholderColor,
            },
          },
          invalid: {
            color: errorColor,
          },
        },
      });

      rebillyLib.on("ready", () => {
        setRebillyReady(true);
        onReady();
      });
    }
  }, [rebillyLib, publicKey, debug, onReady, inputColor, placeholderColor, errorColor, locale]);

  useEffect(() => {
    if (isRebillyControllerReady(rebillyLib)) {
      rebillyLib.update({
        locale: locale,
      });
    }
  }, [formatMessage, locale, rebillyLib]);

  useEffect(() => {
    validateForm();
  }, [validateForm, locale]);

  const onRebillyFieldEvent = useCallback(
    (event, data) => {
      debug("onRebillyFieldEvent", event, JSON.stringify(data, null, 2));
      dispatch({ event, data });
      validateForm();
    },
    [dispatch, validateForm, debug]
  );

  // if there's a space at the top of the user's name, this will cause an error with Rebilly
  // this function checks for spaces at the top of the new value and removes if they exist
  const handleTrimStartOnChange = (e) => {
    if (/^\s/.test(e.target.value) && e.target.value.trimStart) {
      e.target.value = e.target.value.trimStart(); // remove space(s) at top
    }
    handleChange(e); // pass back to Formik to handle change
  };

  if (!rebillyReady) {
    return <FullPageSpinner />;
  }

  return (
    <Stack spacing={3} direction="column" as={Form} ref={formRef} data-testid="rebilly-form">
      <AcceptedCreditCards />

      <Stack spacing={3} direction="column">
        <RebillyCardTextField
          lib={rebillyLib}
          onEvent={onRebillyFieldEvent}
          formState={formState}
          errors={errors}
        />
      </Stack>

      <Heading size="sm">
        <FormattedMessage defaultMessage="Billing Information" description="Section heading" />
      </Heading>
      <Stack spacing={3} direction="column">
        <TextField
          label={formatMessage({
            defaultMessage: "Full Name",
            description: "field label",
          })}
          name="fullname"
          data-rebilly="fullName"
          onChange={handleTrimStartOnChange}
        />
        <AddressWithPlacesAutocomplete
          label={formatMessage({
            defaultMessage: "Address",
            description: "field label",
          })}
        />

        {/* Recreate address data as hidden form for Rebilly library */}
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-address"
          value={values.street || ""}
          data-rebilly="address"
        />
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-address2"
          value={values.street2 || ""}
          data-rebilly="address2"
        />
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-country"
          value={values.country || ""}
          data-rebilly="country"
        />
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-city"
          value={values.city || ""}
          data-rebilly="city"
        />
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-region"
          value={toStateISO(values.country, values.state) || ""}
          data-rebilly="region"
        />
        <VisuallyHiddenInput
          readOnly
          data-testid="rebilly-postalcode"
          value={values.zip || ""}
          data-rebilly="postalCode"
        />

        <TextField
          label={formatMessage({
            defaultMessage: "Email",
            description: "field label",
          })}
          name="email"
          type="email"
          data-rebilly="emails"
          isOptional
        />
      </Stack>

      <Stack spacing={3} justifyContent="center" direction="row" pt={6}>
        <Button
          width={Boolean(onCancel) ? "100%" : "auto"}
          minW={!onCancel && 150}
          data-testid="add-cc-submit-button"
          isDisabled={!isValid || isSubmitting}
          isLoading={isSubmitting}
          loadingText={formatMessage({
            defaultMessage: "Adding...",
            description: "loading label for button",
          })}
          type="submit"
        >
          <FormattedMessage defaultMessage="Add" description="button copy" />
        </Button>
        {onCancel && (
          <Button
            data-testid="add-cc-cancel-button"
            isDisabled={isSubmitting}
            onClick={onCancel}
            variant="outline"
            width="full"
          >
            <FormattedMessage defaultMessage="Cancel" description="button copy" />
          </Button>
        )}
      </Stack>

      {submitError && (
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          {submitError}
        </Alert>
      )}

      <AddCCLegalese />

      {addOtherPMs ? (
        <>
          <Box position="relative" pt="4" pb="8">
            <Divider color="gray.500" h="12px" />
            <AbsoluteCenter background="background" px="4">
              <Text color="primaryColor" fontWeight="500">
                <FormattedMessage defaultMessage="Or" description="Label" />
              </Text>
            </AbsoluteCenter>
          </Box>
          <Stack spacing="4" pb="8">
            {config.addACH && <AddPlaidACHButton />}
            {config.addPaypal && <AddPaypalButton />}
          </Stack>
        </>
      ) : null}
    </Stack>
  );
};

// --------------------------------------------------------------------

const initialState = {
  cardNumber: {
    isValid: false,
    isTouched: false,
    hasFocus: false,
    lastChange: null,
    isCompleted: false,
  },
  cardExpiry: {
    isValid: false,
    isTouched: false,
    hasFocus: false,
    lastChange: null,
    isCompleted: false,
  },
  cardCvv: {
    isValid: false,
    isTouched: false,
    hasFocus: false,
    lastChange: null,
    isCompleted: false,
  },
};

const isValid = (data = {}) => data.completed && data.valid;

const formReducer = (debug) => (state, action) => {
  debug("formReducer:action,state", action, state);
  const { event, data } = action;
  debug(
    `formReducer:completed: ${data.completed}, valid: ${data.valid}, error: ${JSON.stringify(
      data.error
    )}`
  );

  switch (event) {
    case "change":
      return R.pipe(
        R.assocPath([data.name, "isValid"], isValid(data)),
        R.assocPath([data.name, "isCompleted"], data.completed),
        R.assocPath([data.name, "lastChange"], data)
      )(state);
    case "focus":
      return R.pipe(
        R.assocPath([data.name, "hasFocus"], true),
        R.assocPath([data.name, "isTouched"], true)
      )(state);
    case "blur":
      return R.assocPath([data.name, "hasFocus"], false, state);
    default:
      return state;
  }
};

//[{"label":"Emails","value":"stout@lindenlab.com"}]
const parseEmail = (rebillyEmailArray = []) => R.path([0, "value"], rebillyEmailArray);

const rebillyToPaymentData = (values) => {
  const { billingAddress, paymentInstrument, id } = values;
  const payload = {
    // ----------- customer data
    firstName: billingAddress.firstName,
    lastName: billingAddress.lastName,
    address: billingAddress.address,
    address2: billingAddress.address2,
    city: billingAddress.city,
    region: billingAddress.region,
    country: billingAddress.country,
    postalCode: billingAddress.postalCode,
    organization: "",
    email: parseEmail(billingAddress.emails),
    // ---------- card data
    token: id,
    bin: String(paymentInstrument.bin),
    last4: paymentInstrument.last4,
    expMonth: paymentInstrument.expMonth,
    expYear: paymentInstrument.expYear,
    brand: paymentInstrument.brand,
    // ---------- geoIP data
    // ip_address: values.ipAddress,
    // geoip_state: values.geoIPState,
    // geoip_country: values.geoIPCountry,
  };
  return payload;
};

const isRebillyFullNameError = (e) => {
  // according to previous code, it looks like this used to be based on error.code == "fullname-required", but apparently that doesn't work anymore, and this is how it's done now...
  // updated note: apparently we need to check for multiple cases of the full name error from rebilly
  const firstCheck = e && e.code === "fullname-required";
  const secondCheck =
    e &&
    e.details &&
    Array.isArray(e.details) &&
    e.details.includes("fullName value must contain first name and last name");
  return firstCheck || secondCheck;
};

const invalidEmail = R.propEq("field", "billingAddress.emails.0.value");

const isRebillyEmailError = (error) => {
  const isApiValidationError = R.propEq("code", "api-validation", error);
  // any element of the invalid fields array has a field of 'billingAddress.emails.0.value'?
  const isEmailError = R.pipe(R.prop("invalidFields"), R.defaultTo([]), R.any(invalidEmail))(error);
  return isApiValidationError && isEmailError;
};

export default function RebillyForm({
  rebillyLib,
  settings = {},
  onReady,
  onComplete,
  onCancel,
  config,
  hideCancelButton,
}) {
  const { formatMessage } = useIntl();
  const [submitError, setSubmitError] = useState(null);
  const { sessionCtx, sessionId, debug } = useSessionCtx();
  const { sessionJWT } = useSessionJWT();
  const { public_key: publicKey } = settings;
  const formRef = useRef(null);

  const ErrorMessages = formatErrorMessages(formatMessage);

  const getLocalizedErrorMessage = useCallback(
    (code, defaultMessage) => {
      const localizeByCode = {
        [rebillyLib.errorCodes.invalidCardNumber]: formatMessage({
          defaultMessage: "A valid card number is required",
          description: "error field message",
        }),
        [rebillyLib.errorCodes.invalidExpiration]: formatMessage({
          defaultMessage: "A valid expiration is required",
          description: "error field message",
        }),
        [rebillyLib.errorCodes.invalidExpirationYearInFuture]: formatMessage({
          defaultMessage: "A valid expiration is required",
          description: "error field message",
        }),
        [rebillyLib.errorCodes.incompleteCvv]: formatMessage({
          defaultMessage: "A valid CVV is required",
          description: "error field message",
        }),
        [rebillyLib.errorCodes.invalidExpirationYearInPast]: formatMessage({
          defaultMessage: "Your card has expired",
          description: "error field message",
        }),
      };
      return localizeByCode[code] || defaultMessage;
    },
    [rebillyLib, formatMessage]
  );

  useEffect(() => {
    logInfo(sessionJWT, { flowLoc: "REBILLY_INIT" });
  }, [sessionJWT]);

  const [formState, dispatch] = useReducer(formReducer(debug), initialState);

  const initialValues = {
    fullname: "",
    street: "",
    street2: "",
    city: "",
    country: "",
    state: "",
    zip: "",
    email: "",
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
  };

  const [threeDSData, setThreeDSData] = useState(undefined);

  // decides if 3ds is needed and updates state to show component
  // returns true if required
  const trigger3DS = (finishResponse) => {
    if (requires3DS(settings, finishResponse.payload)) {
      debug("3ds required");
      const approvalUrl = R.path(["payload", "approval_url"], finishResponse);
      const paymentMethodId = R.path(["payload", "id"], finishResponse);
      setThreeDSData({ approvalUrl, paymentMethodId });
      return true;
    }
    debug("3ds not required");
    return false;
  };

  const finishCreate = async (values, actions) => {
    let flowLoc = "FINISH_CREATE";
    setSubmitError(null);

    try {
      flowLoc = "FINISH_CREATE.REBILLY_CREATETOKEN";
      const rebillyResponse = await rebillyLib.createToken(formRef.current);
      debug("rebillyResponse: ", JSON.stringify(rebillyResponse || {}));

      flowLoc = "FINISH_CREATE.PAYMENTS_PAYMENTMETHOD_REBILLY";
      const payload = { ...rebillyToPaymentData(rebillyResponse) };
      if (sessionId) {
        payload.session_id = sessionId;
      }
      const finishResponse = await finishCreateCCRebilly(
        sessionJWT,
        payload
      );
      debug("finishCreateResponse: ", JSON.stringify(finishResponse));

      flowLoc = "FINISH_CREATE";
      if (!trigger3DS(finishResponse)) {
        debug("passed 3DS check, onCompleting...");
        onComplete(finishResponse);
      }
    } catch (error) {
      debug("ERROR", error);
      const containsRebillyErr = isRebillyFullNameError(error);
      const containsEmailErr = isRebillyEmailError(error);
      let containsRebillyDescriptiveErr = false; // set default to false, forEach loop below will switch if necessary

      if (containsRebillyErr) {
        actions.setFieldError("fullname", ErrorMessages.FirstAndLastNameRequired());
      }
      if (containsEmailErr) {
        actions.setFieldError("email", ErrorMessages.InvalidEmailAddress());
      }
      if (error.details && error.details.length) {
        const errFieldMap = {
          // need to map Rebilly fields to our Formik field setup
          address: "street",
          address2: "street2",
          region: "state",
          city: "city",
          postalCode: "zip",
        };
        error.details.forEach((d) => {
          const field = d["data-rebilly"] && errFieldMap[d["data-rebilly"]]; // do we have a field we know?
          const errMsg = d.error;
          if (field && errMsg) {
            actions.setFieldError(field, errMsg);
            containsRebillyDescriptiveErr = true; // we have at least 1 rebilly descriptive error
          }
        });
      }
      const unknownErr = !containsRebillyErr && !containsEmailErr && !containsRebillyDescriptiveErr;
      if (unknownErr) {
        logError(sessionJWT, {
          sessionCtx,
          error,
          flowLoc,
          values,
        });
        if (error.status === 402) {
          setSubmitError(
            formatMessage({
              defaultMessage:
                "Your payment could not be processed. Please try a different payment method or contact your card issuer.",
              description: "Alert for card declined during Add Card",
            })
          );
        } else {
          setSubmitError(
            formatMessage({
              defaultMessage: "There was an error. Please try again.",
              description: "Alert for Add Card submission error",
            })
          );
        }
      }
    }
  };

  const handleFinalStatus = (paymentMethodResponse) => {
    debug("3DS_FINAL_STATUS: ", paymentMethodResponse);
    logInfo(sessionJWT, { flowLoc: "3DS_FINAL_STATUS", paymentMethodResponse });
    if (isNilOrEmpty(paymentMethodResponse?.payload)) {
      onCancel();
    } else {
      onComplete(paymentMethodResponse);
    }
  };

  const handleThreeDSError = (isFinal) => {
    debug("3DS_ERROR", isFinal);
    logError(sessionJWT, { flowLoc: "3DS_ERROR", isFinal });
    setThreeDSData(undefined);
    if (isFinal) {
      onCancel();
    }
    // else stay here, maybe the user wants to try again?
  };

  const requiredErrorMessage = () =>
    formatMessage({
      defaultMessage: "This field is required",
      description: "Input validation error",
    });

  const maxStringErrorMessage = ({ max }) =>
    formatMessage(
      {
        defaultMessage: "This field cannot be longer than {max} characters",
        description: "Input validation error",
      },
      {
        max,
      }
    );

  return (
    <>
      <ThreeDS2
        settings={settings}
        data={threeDSData}
        onFinalStatus={handleFinalStatus}
        onError={handleThreeDSError}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape({
          // TODO: Add regex to match expectations on Rebilly's side (allowing word chars for all languages, etc)
          fullname: yup.string().required(requiredErrorMessage).max(90, maxStringErrorMessage),

          googlePlacesAutocomplete: makeGooglePlaceInputValidation("street", {
            required: requiredErrorMessage,
          }),

          street: yup.string().required(requiredErrorMessage).max(60, maxStringErrorMessage),

          street2: yup
            .string()
            // not required
            .max(60, maxStringErrorMessage),

          city: yup.string().required(requiredErrorMessage).max(45, maxStringErrorMessage),

          state: yup
            .string()
            .when("country", (country, schema) => {
              if (!countryHasStates(country)) return schema;

              return schema.required().test(
                "valid-state-for-country",
                () =>
                  formatMessage({
                    defaultMessage: "A valid state is required for the selected country",
                    description: "Input validation error for state",
                  }),
                (state) => isValidState(country, state)
              );
            })
            .max(45, maxStringErrorMessage),

          country: yup.string().required(requiredErrorMessage),

          zip: yup.string().required(requiredErrorMessage).max(10, maxStringErrorMessage),

          email: yup.string().max(255, maxStringErrorMessage),

          cardNumber: yup.string().test({
            message: "${msg}",
            params: {
              msg: getLocalizedErrorMessage(
                getErrorCodeFromState(formState.cardNumber),
                validateRebillyField(formState.cardNumber)
              ),
            },
            test: () => isNilOrEmpty(getErrorCodeFromState(formState.cardNumber)), // TODO: This returns as valid when 'lastChange' does not exist as no interaction with field has occured.
          }),

          cardExpiry: yup.string().test({
            message: "${msg}",
            params: {
              msg: getLocalizedErrorMessage(
                getErrorCodeFromState(formState.cardExpiry),
                validateRebillyField(formState.cardExpiry)
              ),
            },
            test: () => isNilOrEmpty(getErrorCodeFromState(formState.cardExpiry)), // TODO: This returns as valid when 'lastChange' does not exist as no interaction with field has occured.
          }),

          cardCvv: yup.string().test({
            message: "${msg}",
            params: {
              msg: getLocalizedErrorMessage(
                getErrorCodeFromState(formState.cardCvv),
                validateRebillyField(formState.cardCvv)
              ),
            },
            test: () => isNilOrEmpty(getErrorCodeFromState(formState.cardCvv)), // TODO: This returns as valid when 'lastChange' does not exist as no interaction with field has occured.
          }),
        })}
        onSubmit={finishCreate}
        validateOnMount
      >
        {(formikProps) => (
          <InnerForm
            {...formikProps}
            formState={formState}
            dispatch={dispatch}
            rebillyLib={rebillyLib}
            publicKey={publicKey}
            onCancel={!hideCancelButton && onCancel}
            onReady={onReady}
            formRef={formRef}
            submitError={submitError}
            config={config}
          />
        )}
      </Formik>
    </>
  );
}
